import React, {useCallback, useEffect, useState} from 'react';
import { useSDK, useAutoResizer } from '@contentful/react-apps-toolkit';
import {fetchStatus} from '../services/translated';
import SidebarTranslationEnabled from '../components/SidebarTranslationEnabled';
import SidebarTranslationInProgress from '../components/SidebarTranslationInProgress';
import Loading from '../components/Loading';
import {Button} from "@contentful/f36-components";
import PoweredBy from "../components/PoweredBy";
import {groupBy} from "lodash/collection";
import {getStatusFromRequests} from "../utils";
import {LandingPage} from "../components/landing-page/LandingPage";

const STATUS_TRANSLATION_AVAILABLE = 'translation-available'
const STATUS_TRANSLATION_IN_QUEUE = 'translation-in-queue'
const STATUS_TRANSLATION_IN_PROGRESS = 'translation-in-progress'

const IN_QUEUE_STATUSES = ["upload", "wc raw", "ingested", "bucketing"]

const Sidebar = () => {
  useAutoResizer();

  const [isLandingPage, setIsLandingPage] = useState(true);
  const [entryStatus, setEntryStatus] = useState({})
  const [orderList, setOrderList] = useState([]);

  const sdk = useSDK();

  let apiKey = sdk.parameters.installation.apiKey
  const {space, environment, id: entryId} = sdk.entry.getSys();

  const getAndSetEntryStatus = useCallback(async () => {
    const {
      pendingTasks: currentEntryRequests,
      relatedTasks,
      mainTasks
    } = await fetchStatus({
      apiKey,
      entryId,
      spaceId: space.sys.id,
      environmentId: environment.sys.id
    })

    let newEntryStatus
    // If there are no pending tasks for this entry, entry could be translated
    if(currentEntryRequests.length === 0 && relatedTasks.length === 0 && mainTasks.length === 0) {
      newEntryStatus = STATUS_TRANSLATION_AVAILABLE
    } else {
      // Order can be cancelled if it started from this entry and ALL tasks (of this entry and its related) are in "wc raw"
      // or "bucketing status

      // If all the pending requests related to this entry are in a queue status, current entry is in queue and the order can be deleted
      if (
        [currentEntryRequests, relatedTasks, mainTasks]
          .filter(a => a.length > 0)
          .flat()
          .every(({ status }) => IN_QUEUE_STATUSES.includes(status))
      ) {
        newEntryStatus = STATUS_TRANSLATION_IN_QUEUE
      } else {
        newEntryStatus = STATUS_TRANSLATION_IN_PROGRESS
      }

    }

    setEntryStatus(newEntryStatus)
    setOrderList(currentEntryRequests);
  }, [apiKey, entryId, space.sys.id, environment.sys.id])

  useEffect(() => {
    getAndSetEntryStatus().catch(err => console.error(err));
  }, [getAndSetEntryStatus]);

  const updateEntryStatus = async (translationType) => {
    if (translationType !== 'MT') {
      getAndSetEntryStatus().catch(err => console.error(err));
    } else {
      // Return to the landing page if MT order is completed
      setEntryStatus(STATUS_TRANSLATION_AVAILABLE);
      setIsLandingPage(true);
    }
  }

  const onNewOrder = () => {
    setIsLandingPage(false);
    setEntryStatus(STATUS_TRANSLATION_AVAILABLE);
  }

  // Group orders by order group id to get the available/busy target languages for the new order
  const ordersByIdOrderGroup = groupBy(orderList, 'id_order_group');
  const ordersStatus = Object.entries(ordersByIdOrderGroup).map(([idOrderGroup, orders]) => {
    return {
      idOrderGroup,
      status: getStatusFromRequests(orders),
      targetLanguages: [...new Set(orders.map(({ target_language }) => target_language))],
    }
  });
  const busyLanguages = ordersStatus
      .filter(({ status }) => status === 'IN PROGRESS' || status === 'IN QUEUE')
      .map(({ targetLanguages }) => targetLanguages).flat();
  const languagesInProgressOrInQueue = [...new Set(busyLanguages)];

  const switchView = () => {
    if (orderList.length > 0) {
        setEntryStatus(STATUS_TRANSLATION_IN_PROGRESS);
    } else {
        setEntryStatus(STATUS_TRANSLATION_AVAILABLE);
        setIsLandingPage(true);
    }
  }

  switch (entryStatus) {
    case STATUS_TRANSLATION_AVAILABLE:
      if (isLandingPage) {
        return <LandingPage onNewTranslation={() => setIsLandingPage(false)} />
      } else {
        return <SidebarTranslationEnabled
            onCancel={switchView}
            busyLanguages={languagesInProgressOrInQueue}
            onTranslationOrderSent={updateEntryStatus}
        />
      }
    case STATUS_TRANSLATION_IN_QUEUE:
    case STATUS_TRANSLATION_IN_PROGRESS:
      return (
          <>
            <SidebarTranslationInProgress orderList={orderList} />
            <Button variant="primary" style={{marginTop: '20px'}} isFullWidth onClick={onNewOrder}>New translation order</Button>
            <PoweredBy/>
          </>
      );
    default:
      return <Loading />
  }

};

export default Sidebar;
